import { useMutation } from '@apollo/client';
import { Controller, FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { graphql } from '~/apollo/generated/v4';
import { Modal } from '~/components/common/Modal';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { OutcropSelectV4 } from '~/components/upload/outcrop/outcrop-select-v4';
import { useModalState } from '~/hooks/modal';
import { useZodForm } from '~/utils/forms';
import type { RefetchQueries } from '~/utils/graphql';
import { ppNumberOrNull } from '~/utils/zod';

export const CREATE_PALEO_MAP_OUTCROP_LINK = graphql(`
  mutation CreatePaleoMapOutcropLink($input: PaleoMapAddOutcropLinkInput!) {
    paleoMapAddOutcropLink(input: $input)
  }
`);

type FormValues = {
  outcropId: string;
};
const initialValues = (): FormValues => ({ outcropId: '' });

const schema = z.object({
  outcropId: z.preprocess(ppNumberOrNull, z.number().int().positive()),
});

export function CreatePaleoMapOutcropLink({
  children,
  paleoMapId,
  currentOutcropIds,
  refetchQueries,
}: {
  children: (showModal: () => void) => JSX.Element;
  paleoMapId: number;
  /** Existing outcrops on the paleoMap that should be filtered out */
  currentOutcropIds: number[];
  refetchQueries: RefetchQueries;
}) {
  const { show, showModal, hideModal } = useModalState();
  const [createLink, { loading, error }] = useMutation(
    CREATE_PALEO_MAP_OUTCROP_LINK,
    { refetchQueries },
  );

  const form = useZodForm({
    schema,
    defaultValues: initialValues(),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await createLink({
        variables: {
          input: {
            paleoMapId,
            outcropId: values.outcropId,
          },
        },
      });
      toast.success('Outcrop linked successfully.');
      form.reset(initialValues());
      hideModal();
    } catch (err) {
      console.log('Error creating outcrop link', err);
      toast.error('There was a problem creating the link.');
    }
  });

  return (
    <>
      {children(showModal)}

      <Modal open={show} onHide={hideModal}>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit}>
            <Modal.Body heading="Link Outcrop">
              <div className="space-y-4">
                <Controller
                  control={form.control}
                  name="outcropId"
                  render={({ field }) => (
                    <OutcropSelectV4
                      {...field}
                      disabledIds={currentOutcropIds}
                    />
                  )}
                />
                <HookFormErrors graphQLError={error} />
              </div>
            </Modal.Body>

            <Modal.Footer>
              <Button
                type="button"
                color="ghost"
                disabled={loading}
                onClick={hideModal}
              >
                Cancel
              </Button>

              <Button type="submit" color="primary" loading={loading}>
                Save
              </Button>
            </Modal.Footer>
          </form>
        </FormProvider>
      </Modal>
    </>
  );
}
